import {
  AfterContentInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatSelect } from '@angular/material/select';

@Component({
  selector: 'kt-general-autocomplete',
  templateUrl: './general-autocomplete.component.html',
  styleUrls: ['./general-autocomplete.component.scss'],
})
export class GeneralAutocompleteComponent implements OnInit, OnDestroy, AfterContentInit {
  filteredItems = of([]);
  controlObject = new FormControl();
  controlObjectVal = new FormControl();
  readonly term$ = new BehaviorSubject('');
  isLoading = new BehaviorSubject(false);
  focusInBS = new BehaviorSubject(false);
  reload = new BehaviorSubject(false);
  inputCtrl = new FormControl();
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  private _loadItemsFunction;
  @Input() set loadItemsFunction(fn) {
    this._loadItemsFunction = fn;
    this.searchField.value = '';
  }
  get loadItemsFunction() {
    return this._loadItemsFunction;
  }
  @Input() dataBefore;
  @Input() dataAfter;
  @Input() filterResultsFunction;
  @Input() optionSearchFields;
  @ViewChild('searchField', { static: true }) searchField: MatSelect;
  @Input() optionHeader;
  @Input() optionFormatter;
  @Input() chipOptionFormatter;
  @Input() valueFormatter;
  @Input() panelWidth = 'auto';
  @Input() required;
  @Input() showError = true;
  @Input() readOnlyTheme;
  @Input() control;
  private _readOnly: any;
  private loadedItems = [];
  @Input() set readOnly(val) {
    this._readOnly = val;
    if (this.controlObject) {
      if (val) {
        if (this.readOnlyTheme) return;
        this.controlObject.disable();
      } else {
        this.controlObject.enable();
      }
    }
  }

  get readOnly() {
    return this._readOnly;
  }

  @Input() id = '';
  @Input() optionId = 'id';
  @Input() placeholder;
  @Input() dataPath = null;
  @Input() hint = '';
  @Input() hintStrong = '';
  value: any;
  @Input() label: any;
  @Input() appearance = 'fill';
  @Input() chipList = false;
  @Input() formatNewObjectFn;
  @Input() objects = [];
  @ViewChild('recorderInput') recorderInputChipList: ElementRef<HTMLInputElement>;
  @ViewChild('recorderInput1') recorderInput: ElementRef<HTMLInputElement>;
  private sub: any;
  private sub1: any;
  private sub2: any;
  private removeAll: boolean;
  private ctrl: FormControl;
  @Input() change: (value: any) => void = (_value: any) => {
    /*  no-op   */
  };

  ngOnInit(): void {
    if (this.required) this.controlObject = new FormControl('', [this.valueSelected()]);
    const ctrl = this.chipList ? this.inputCtrl : this.controlObject;
    this.ctrl = ctrl;
    ctrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(() =>
          this.getAutocompleteSuggestions().pipe(
            catchError(() => {
              this.isLoading.next(false);
              return [];
            }),
          ),
        ),
      )
      .subscribe((autocompleteList) => {
        if (this.dataPath && autocompleteList[this.dataPath]) {
          autocompleteList = autocompleteList[this.dataPath];
        }
        if (!autocompleteList || !autocompleteList.length) {
          autocompleteList = [];
        }
        if (this.filterResultsFunction) {
          autocompleteList = this.filterResultsFunction(autocompleteList);
        }
        this.filteredItems = of(autocompleteList);
        this.isLoading.next(false);
      });

    this.loadedItems.push(...(this.objects?.length ? this.objects : []));
  }

  ngOnDestroy() {
    if (this.sub instanceof Subscription) {
      this.sub.unsubscribe();
    }
    if (this.sub1 instanceof Subscription) {
      this.sub1.unsubscribe();
    }
    if (this.sub2 instanceof Subscription) {
      this.sub2.unsubscribe();
    }
  }

  selectedRecorder(setNewValue = true) {
    if (this.chipList) {
      if (!this.objects) {
        this.objects = [];
      }
      if (setNewValue) {
        this.objects.push(this.formatNewObjectFn(this.inputCtrl.value));
      }
      this.term$.next('');
      this.controlObject.setValue(this.objects);
      this.loadedItems.push(this.inputCtrl.value);
    }
    this.value = this.chipList ? this.objects : this.controlObject.value;
    this.removeAll = true;
    this.change(this.value);
    if (this.recorderInputChipList && this.recorderInputChipList.nativeElement) {
      this.recorderInputChipList.nativeElement.value = '';
    }
  }
  isSelected(selectedObject: any) {
    if (this.loadedItems)
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < this.loadedItems.length; i++) {
        // if (this.objects[i] === this.formatNewObjectFn(selectedObject)){
        //   return true;
        if (this.compareObjects(this.loadedItems[i], selectedObject)) {
          return true;
        }
      }
    return false;
  }

  compareObjects(object1, object2) {
    if (object1 && object2)
      return !!object1[this.optionId] && object1[this.optionId] === object2[this.optionId];

    return false;
  }

  onChange($event: any) {
    if (
      ($event.keyCode === 8 && this.removeAll) ||
      (!this.removeAll && this.controlObject.value === '')
    ) {
      this.controlObject.setValue('');
      this.inputCtrl.setValue('');
      this.selectedRecorder();
    } else {
      this.removeAll = false;
    }
  }

  private getAutocompleteSuggestions(): Observable<any> {
    this.filteredItems = of([]);

    const ctrl = this.chipList ? this.inputCtrl : this.controlObject;
    if (ctrl.value || ctrl.value === '') {
      if (typeof ctrl.value === 'string') {
        this.isLoading.next(true);
        return this.searchField?.value
          ? this.loadItemsFunction(ctrl.value, this.searchField.value)
          : this.loadItemsFunction(ctrl.value);
      }
    }
    return this.filteredItems;
  }

  valueSelected(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      if (this.chipList) {
        if (!c.value || c.value.length < 1) {
          return { required: true };
        }
        return null;
      } else if (typeof c.value === 'object') {
        if (!c.value) {
          return { required: true };
        }
        return null;
      }
      return { required: true };
    };
  }

  updateValueAndValidity() {
    this.controlObject.updateValueAndValidity();
    this.controlObject.markAsTouched();
  }

  setValue(newValue) {
    const res = this.chipList ? (newValue ? [...newValue] : []) : newValue ? { ...newValue } : null;

    this.removeAll = true;
    if (this.chipList) {
      this.objects = res;
      this.loadedItems.push(...res);
      this.controlObject.setValue(res);
    } else {
      this.controlObject.setValue(res);
    }
    if (this.sub instanceof Subscription) {
      this.sub.unsubscribe();
    }
    if (this.sub1 instanceof Subscription) {
      this.sub1.unsubscribe();
    }
    if (this.chipList) {
      this.filteredItems = of(res);
    } else {
      this.filteredItems = of([res]);
    }
    this.selectedRecorder(false);
  }

  onFocus() {
    this.focusInBS.next(!this.focusInBS.value);

    this.sub = this.focusInBS.subscribe(() => {
      this.isLoading.next(true);

      setTimeout(() => {
        this.sub1 = (
          this.controlObject.value
            ? this.loadItemsFunction(this.term$.value || '', this.controlObject.value)
            : this.loadItemsFunction(this.term$.value || '')
        )
          .pipe(
            finalize(() => {
              this.isLoading.next(false);
              return [];
            }),
          )
          .subscribe((autocompleteList) => {
            if (this.dataPath && autocompleteList[this.dataPath]) {
              autocompleteList = autocompleteList[this.dataPath];
            }
            if (!autocompleteList || !autocompleteList.length) {
              autocompleteList = [];
            }
            if (this.filterResultsFunction) {
              autocompleteList = this.filterResultsFunction(autocompleteList);
            }
            this.filteredItems = of(autocompleteList);
            this.isLoading.next(false);
            this.sub.unsubscribe();
          });
      });
    });
  }

  handleBlur() {
    if (this.chipList) {
      this.recorderInputChipList.nativeElement.value = '';
    } else {
      this.term$.next(typeof this.controlObject.value === 'object' ? '' : this.controlObject.value);
    }
  }

  remove(entity) {
    if (!entity) return;

    const isObject = typeof entity === 'object';
    const objectIdx = this.objects.findIndex((o) =>
      o ? (isObject ? o[this.optionId] === entity[this.optionId] : o === entity) : false,
    );
    const itemIdx = this.loadedItems.findIndex((i) =>
      i ? (isObject ? i[this.optionId] === entity[this.optionId] : i === entity) : false,
    );

    if (itemIdx >= 0) this.loadedItems.splice(itemIdx, 1);

    if (objectIdx >= 0) {
      this.objects.splice(objectIdx, 1);
      this.controlObject.setValue(this.objects);
      this.change(this.objects);
    }
  }

  triggerReload() {
    this.reload.next(true);

    this.sub2 = this.reload.subscribe((val) => {
      if (val) {
        this.filteredItems = of([]);
        this.isLoading.next(true);
        this.searchField?.value
          ? this.loadItemsFunction(
              this.inputCtrl.value ? this.inputCtrl.value : '',
              this.searchField.value,
            )
          : this.loadItemsFunction(this.inputCtrl.value ? this.inputCtrl.value : '');
        this.sub1 = (
          this.searchField?.value
            ? this.loadItemsFunction(this.term$.value || '', this.searchField.value)
            : this.loadItemsFunction(this.term$.value || '')
        )
          .pipe(
            catchError(() => {
              this.isLoading.next(false);
              return [];
            }),
          )
          .subscribe((autocompleteList) => {
            this.isLoading.next(false);
            if (this.dataPath && autocompleteList[this.dataPath]) {
              autocompleteList = autocompleteList[this.dataPath];
            }
            if (!autocompleteList || !autocompleteList.length) {
              autocompleteList = [];
            }
            if (this.filterResultsFunction) {
              autocompleteList = this.filterResultsFunction(autocompleteList);
            }
            this.filteredItems = of(autocompleteList);
          });
      }
    });
  }
  ngAfterContentInit(): void {
    if (this.readOnly) {
      if (this.readOnlyTheme) return;
      this.controlObject.disable();
    }
  }

  isInvalid(): boolean {
    return this.controlObject.touched && this.controlObject.invalid;
  }
  setErrors(val) {
    const nameFormControl = this.controlObject as FormControl;
    nameFormControl.setErrors(val);
  }
}
